import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Skeleton,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { InboxDown } from "@mynaui/icons-react";
import TuneIcon from "@mui/icons-material/Tune";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import RequestCard from "../components/RequestCard";
import requestService from "../../../services/requestService";
import assetService from "../../../services/assetService";
import { useZone } from "../../../shared/context/ZoneContext";

const RequestCardSkeleton = () => (
  <Box sx={{ border: "1px solid #E2E8F0", borderRadius: "12px", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="text" width={200} height={24} />
        <Skeleton variant="text" width={140} height={20} />
      </Box>
      <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: "4px" }} />
    </Box>
    <Skeleton variant="text" width="90%" height={20} />
    <Box sx={{ display: "flex", gap: "32px", mt: 1 }}>
      <Skeleton variant="text" width={80} height={20} />
      <Skeleton variant="text" width={80} height={20} />
      <Skeleton variant="text" width={80} height={20} />
    </Box>
    <Box sx={{ display: "flex", gap: "16px", mt: 1 }}>
      <Skeleton variant="rounded" width="50%" height={39} sx={{ borderRadius: "8px" }} />
      <Skeleton variant="rounded" width="50%" height={39} sx={{ borderRadius: "8px" }} />
    </Box>
  </Box>
);

const selectSx = {
  borderRadius: "8px",
  fontSize: "14px",
  height: "40px",
  background: "#FFFFFF",
  "& fieldset": { borderColor: "#E2E8F0" },
  "&:hover fieldset": { borderColor: "#CBD5E1" },
};

const colLabelSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#374151",
  mb: "8px",
};

const mapApiRequest = (r) => ({
  id: r.id,
  requestNumber: r.requestNumber,
  title: r.workOrderName,
  description: r.workOrderDescription,
  location: [r.plantName, r.areaName, r.assetName].filter(Boolean).join(" › "),
  priority: r.criticality,
  technician: r.assignedTo,
  dueDate: r.dueDate
    ? new Date(r.dueDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—",
  dueDateRaw: r.dueDate ?? null,
  cost: r.partsCost ?? 0,
  parts: r.partsCount ?? 0,
  status: r.workOrderStatus,
});

export default function RequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const ITEMS_PER_PAGE = 10;

  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [criticality, setCriticality] = useState("all");
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [technician, setTechnician] = useState("all");
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo] = useState("");

  const { selectedZone } = useZone();

  const navigate = useNavigate();

  const fetchRequests = useCallback(async () => {
    if (!selectedZone) return;
    setLoading(true);
    setError(null);
    try {
      const res = await requestService.getRequests(selectedZone);
      const items = res.data.data ?? [];
      setRequests(items.map(mapApiRequest));
    } catch (err) {
      setError(err.message || "Failed to load requests.");
    } finally {
      setLoading(false);
    }
  }, [selectedZone]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    requestService.getRequestPriorities()
      .then((res) => {
        const data = res?.data?.data ?? [];
        setPriorityOptions(data.map((p) => p.label));
      })
      .catch(() => {});

    assetService.getTechnicians()
      .then((res) => {
        const users = res?.data?.data?.users ?? [];
        setTechnicianOptions(users.map((u) => u.username));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, criticality, technician, dueDateFrom, dueDateTo]);

  const activeFilterCount = [
    criticality !== "all",
    technician !== "all",
    dueDateFrom !== "" || dueDateTo !== "",
  ].filter(Boolean).length;

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      (request.requestNumber || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (request.title || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCriticality =
      criticality === "all" || (request.priority || "").toLowerCase() === criticality.toLowerCase();
    const matchesTechnician =
      technician === "all" || request.technician === technician;
    const requestDate = request.dueDateRaw ? new Date(request.dueDateRaw) : null;
    const matchesDueDateFrom = !dueDateFrom || (requestDate && requestDate >= new Date(dueDateFrom));
    const matchesDueDateTo = !dueDateTo || (requestDate && requestDate <= new Date(dueDateTo));
    return matchesSearch && matchesCriticality && matchesTechnician && matchesDueDateFrom && matchesDueDateTo;
  });

  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const paginatedRequests = filteredRequests.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleApprove = (id) => {
    requestService.updateRequestStatus(id, 'approved').then(() => fetchRequests()).catch(() => {});
  };

  const handleReject = (id) => {
    requestService.updateRequestStatus(id, 'rejected').then(() => fetchRequests()).catch(() => {});
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 2.5, md: 3, lg: 4 } }}>

        {/* ── Main Card ── */}
        <Box sx={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
        }}>

          {/* ── Header Row ── */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: { xs: "16px", sm: "24px" },
            py: { xs: "14px", sm: "16px" },
          }}>
            {/* Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <InboxDown size={22} />
              <Typography sx={{ fontWeight: 600, fontSize: { xs: "16px", sm: "18px" }, color: "#020618" }}>
                Requests
              </Typography>
            </Box>

            {/* Right: Search + view toggles + Filters button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              {/* Search */}
              <TextField
                placeholder="Search"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: { xs: 1, sm: "0 0 auto" },
                  width: { sm: "260px", md: "360px" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "40px",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                  },
                }}
              />

              {/* Grid view icon */}
              <Box sx={{
                width: 40, height: 40,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid #E2E8F0", borderRadius: "8px",
                background: "#1E293B", cursor: "pointer", flexShrink: 0,
              }}>
                <ViewModuleIcon sx={{ fontSize: 18, color: "#FFFFFF" }} />
              </Box>

              {/* List view icon */}
              <Box sx={{
                width: 40, height: 40,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid #E2E8F0", borderRadius: "8px",
                background: "#FFFFFF", cursor: "pointer", flexShrink: 0,
              }}>
                <TableRowsIcon sx={{ fontSize: 18, color: "#64748B" }} />
              </Box>

              {/* Filters button */}
              <Box
                onClick={() => setFiltersOpen((p) => !p)}
                sx={{
                  display: "flex", alignItems: "center", gap: "8px",
                  px: "16px", height: "40px", flexShrink: 0,
                  borderRadius: "8px",
                  background: "#1E293B",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { background: "#334155" },
                }}
              >
                <TuneIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#FFFFFF" }}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Box sx={{
                    width: "18px", height: "18px",
                    background: "#3B82F6", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#FFF" }}>
                      {activeFilterCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* ── Filter Panel ── */}
          <Collapse in={filtersOpen}>
            <Box sx={{
              px: { xs: "16px", sm: "24px" },
              py: "20px",
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderLeft: "none",
              borderRight: "none",
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}>
              {/* Criticality */}
              <Box sx={{ flex: "1 1 180px", minWidth: "160px" }}>
                <Typography sx={colLabelSx}>Criticality</Typography>
                <FormControl fullWidth size="small">
                  <Select value={criticality} onChange={(e) => setCriticality(e.target.value)} sx={selectSx}>
                    <MenuItem value="all">All Criticalities</MenuItem>
                    {priorityOptions.map((label) => (
                      <MenuItem key={label} value={label.toLowerCase()}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Technician */}
              <Box sx={{ flex: "1 1 180px", minWidth: "160px" }}>
                <Typography sx={colLabelSx}>Technicians</Typography>
                <FormControl fullWidth size="small">
                  <Select value={technician} onChange={(e) => setTechnician(e.target.value)} sx={selectSx}>
                    <MenuItem value="all">All Technicians</MenuItem>
                    {technicianOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Due Date */}
              <Box sx={{ flex: "1 1 280px", minWidth: "240px" }}>
                <Typography sx={colLabelSx}>Due Date</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    size="small" type="date"
                    value={dueDateFrom}
                    onChange={(e) => setDueDateFrom(e.target.value)}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "14px", height: "40px",
                        background: "#FFFFFF",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
                      },
                    }}
                  />
                  <Typography sx={{ color: "#94A3B8", fontSize: "14px", flexShrink: 0 }}>–</Typography>
                  <TextField
                    size="small" type="date"
                    value={dueDateTo}
                    onChange={(e) => setDueDateTo(e.target.value)}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "14px", height: "40px",
                        background: "#FFFFFF",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Collapse>

          {/* ── Cards Grid ── */}
          <Box sx={{ p: { xs: "16px", sm: "24px" } }}>
            {loading ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Box key={i} sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 10px)" }, maxWidth: { xs: "100%", md: "calc(50% - 10px)" } }}>
                    <RequestCardSkeleton />
                  </Box>
                ))}
              </Box>
            ) : error ? (
              <Typography sx={{ color: "#C10007", fontSize: "14px" }}>{error}</Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
                  {filteredRequests.length === 0 ? (
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 6 }}>
                      <Typography sx={{ color: "#62748E", fontSize: "14px" }}>
                        No requests found.
                      </Typography>
                    </Box>
                  ) : (
                    paginatedRequests.map((request, index) => (
                      <Box
                        key={`${request.id}-${index}`}
                        sx={{
                          flex: { xs: "1 1 100%", md: "1 1 calc(50% - 10px)" },
                          maxWidth: { xs: "100%", md: "calc(50% - 10px)" },
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(`/requests/${request.id}`)}
                      >
                        <RequestCard request={request} onApprove={handleApprove} onReject={handleReject} />
                      </Box>
                    ))
                  )}
                </Box>

                {totalPages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: "24px" }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      shape="rounded"
                      sx={{
                        "& .MuiPaginationItem-root": { fontSize: "14px" },
                        "& .Mui-selected": { background: "#1E293B", color: "#fff" },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}
